import { useMemo, useState, useEffect } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, FormLabel, InputLabel, MenuItem, Radio, RadioGroup, Select, Stack, Typography } from "@mui/material";

import {
    calculationIdAtom,
    copyFromConfigAtom,
    destinationIdAtom,
    lotIdAtom,
    phaseIdAtom,
    projectIdAtom,
} from "atoms/Ltool/copyFromConfigAtoms";
import { copyToConfigAtom } from "atoms/Ltool/copyToConfigAtoms";
import { areConfigsEqual } from "utils/utilsLtool";
import { ProjectSelect } from "utils/projectSelectorLtool/ProjectSelect";
import { DestinationSelect } from "utils/projectSelectorLtool/DestinationSelect";
import { LotSelect } from "utils/projectSelectorLtool/LotSelect";
import { PhaseSelect } from "utils/projectSelectorLtool/PhaseSelect";
import { CalculationSelect } from "utils/projectSelectorLtool/CalculationSelect";
import { isConfigComplete } from "utils/utilsLtool";
import { errorAtom } from "atoms/error";
import { useSnackbar } from 'utils/hooks/useSnackbar';
import { authTokenAtom } from "atoms/authAtoms";
import { powertrainAtom } from "atoms/powertrainAtom";
import { gearIdAtom } from "atoms/gearAtom";
import { submitAllCopyProject, submitAllCopyProjectHev, submitSubCopyProject, submitUnitCopyProject, submitUnitCopyProjectHev, submitSubCopyProjectHev, type FailedCopyItem, type LRegistrationBatchResponse } from "api/submitLtool";
import { fetchGears, fetchCalculationSet } from "api/fetchLtool";
import { PHENOMENON_GROUP_IDS, UNIT_IDS } from "utils/constsLtool";
import { CONDITION_GROUP_IDS as HEV_CONDITION_GROUP_IDS, GEAR_IDS } from "utils/constsHev";
import { mappingAtom } from "atoms/mappingAtoms";
import { buildLabeledOptions } from "utils/mappingLabel";
import { naturalSortBy } from "utils/sort";
import { toUserMessage } from "utils/apiErrorMessage";
import { FailedItemsList, buildCopyErrorMessage, classifySummary, buildResultMessage } from "components/FailedItemsList";

export const ProjectCopy: React.FC = () => {
    const authToken = useAtomValue(authTokenAtom);
    const config = useAtomValue(copyToConfigAtom);
    const fromConfig = useAtomValue(copyFromConfigAtom);
    const setError = useSetAtom(errorAtom);
    const { showSuccess, showError, showWarning } = useSnackbar();
    const mapping = useAtomValue(mappingAtom);
    const powertrain = useAtomValue(powertrainAtom);
    const gearId = useAtomValue(gearIdAtom);

    // HEV: 全データコピーはコピー元の登録済み全 gear を列挙し gear ごとに POST する（#20、gear 選択に依存しない）。
    // unit/個別はコピー先 gear=選択中の gearId、コピー元 gear=ダイアログ選択（#23）。condition_group は gn/vib のみ。
    const isHev = powertrain === 'hev';
    const groupIds: readonly string[] = isHev ? HEV_CONDITION_GROUP_IDS : PHENOMENON_GROUP_IDS;

    // unit は定義順（マトリクスの行順と一致）、condition_group は表示ラベル基準で並べる（mapping 取得後に確定）
    const unitOptions = useMemo(
        () => buildLabeledOptions(mapping, 'unit_id', UNIT_IDS),
        [mapping],
    );
    const groupOptions = useMemo(
        () => naturalSortBy(buildLabeledOptions(mapping, 'condition_group_id', groupIds), (o) => o.label),
        [mapping, groupIds],
    );

    const [loading, setLoading] = useState(false);
    const [copyOpen, setCopyOpen] = useState(false);
    const [copyMode, setCopyMode] = useState<"all" | "unit" | "sub">("all");

    const [type, setType] = useState<"target" | "measurement">("target");
    const [unit_id, setUnitId] = useState<string>("");
    const [conditionGroupId, setConditionGroupId] = useState<string>("");
    const [srcGearId, setSrcGearId] = useState<string>("");
    const [fromGears, setFromGears] = useState<string[]>([]);
    // コピー元 gear の取得状態。0 件提示は 'success' かつ空のときのみ行い、取得中/取得失敗とは区別する。
    const [fromGearsStatus, setFromGearsStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [failedItems, setFailedItems] = useState<FailedCopyItem[]>([]);
    const [copyMessage, setCopyMessage] = useState<string | null>(null);

    // HEV unit/sub コピー: unit_id が選ばれたらコピー元 calc の登録済み gear を取得する。
    useEffect(() => {
        if (!isHev || !unit_id || !fromConfig.calculation_id) {
            setFromGears([]);
            setSrcGearId("");
            setFromGearsStatus('idle');
            return;
        }
        let active = true; // unit 変更前の古い結果が現在の状態を上書きしないようにする
        setFromGears([]);
        setSrcGearId("");
        setFromGearsStatus('loading');
        fetchGears(authToken, fromConfig, unit_id)
            .then((gears) => {
                if (!active) return;
                setFromGears(gears);
                setFromGearsStatus('success');
            })
            .catch(() => {
                if (!active) return;
                setFromGears([]);
                setFromGearsStatus('error');
            });
        return () => { active = false; };
    }, [isHev, unit_id, fromConfig.calculation_id]); // eslint-disable-line react-hooks/exhaustive-deps

    const selectedPath = [
        fromConfig.project_id,
        fromConfig.destination_id,
        fromConfig.lot_id,
        fromConfig.phase_id,
        fromConfig.calculation_id
    ].filter(id => id).join(' / ');

    const targetConfigComplete = isConfigComplete(config);
    const sourceConfigComplete = isConfigComplete(fromConfig);
    const sameAsTarget = areConfigsEqual(config, fromConfig);

    const handleDialogClose = () => {
        setCopyOpen(false);
        setFailedItems([]);
        setCopyMessage(null);
        setUnitId("");
        setSrcGearId("");
    };

    // HEV 全データコピー: コピー元計算セットの登録済み gear を unit 横断の和集合で列挙する。
    const collectSourceGears = async (): Promise<string[]> => {
        const { units } = await fetchCalculationSet(authToken, fromConfig, "hev");
        const gearSet = new Set<string>();
        for (const u of units) {
            const gears = await fetchGears(authToken, fromConfig, u);
            gears.forEach((g) => gearSet.add(g));
        }
        return Array.from(gearSet);
    };

    // gear ごとの結果を 1 つの LRegistrationBatchResponse に集約する
    // （最初の 4xx を保持し、failed_items と summary（成功分のみ）を連結・合算する）。
    const aggregateResponses = (responses: LRegistrationBatchResponse[]): LRegistrationBatchResponse => {
        const firstError = responses.find((r) => r.status >= 400);
        const failed = responses.flatMap((r) => r.failed_items ?? []);
        const summaries = responses.filter((r) => r.status < 400 && r.summary).map((r) => r.summary!);
        const summary = summaries.length > 0
            ? summaries.reduce(
                (acc, s) => ({
                    succeeded: acc.succeeded + s.succeeded,
                    failed: acc.failed + s.failed,
                    skipped: acc.skipped + s.skipped,
                }),
                { succeeded: 0, failed: 0, skipped: 0 },
            )
            : undefined;
        return {
            status: firstError ? firstError.status : 200,
            message: firstError?.message,
            summary,
            failed_items: failed,
        };
    };

    const handleConfirm = async () => {
        if (!sourceConfigComplete) {
            showWarning("コピー元の計算セットを選択してください。");
            return;
        }
        // 全データコピーは gear を列挙するため、画面上部の選択 gear に依存しない（unit/個別のみ必須）。
        if (isHev && copyMode !== "all" && !gearId) {
            showWarning("HEV では Gear を選択してください（データ登録画面で選択）。");
            return;
        }
        setLoading(true);
        setError(null);
        setFailedItems([]);
        setCopyMessage(null);
        try {
            let response;
            if (copyMode === "all") {
                if (isHev) {
                    // #20: コピー元の登録済み全 gear を列挙し gear ごとに逐次 POST、結果を集約する。
                    const gears = await collectSourceGears();
                    if (gears.length === 0) {
                        setCopyMessage("コピー元に登録済みの Gear がありません。");
                        return;
                    }
                    const responses: LRegistrationBatchResponse[] = [];
                    for (const g of gears) {
                        responses.push(await submitAllCopyProjectHev(authToken, config, fromConfig, g, type));
                    }
                    response = aggregateResponses(responses);
                } else {
                    response = await submitAllCopyProject(authToken, config, fromConfig, type);
                }
            } else if (copyMode === "unit") {
                response = isHev
                    ? await submitUnitCopyProjectHev(authToken, config, fromConfig, gearId!, srcGearId, unit_id, type)
                    : await submitUnitCopyProject(authToken, config, fromConfig, unit_id, type);
            } else {
                response = isHev
                    ? await submitSubCopyProjectHev(authToken, config, fromConfig, gearId!, srcGearId, unit_id, conditionGroupId, type)
                    : await submitSubCopyProject(authToken, config, fromConfig, unit_id, conditionGroupId, type);
            }
            if (response.status >= 400) {
                setCopyMessage(buildCopyErrorMessage(response));
                setFailedItems(response.failed_items ?? []);
                return;
            }
            const failed = response.failed_items ?? [];
            if (!response.summary) {
                if (failed.length > 0) {
                    setCopyMessage("一部の登録に失敗しました。");
                    setFailedItems(failed);
                    return;
                }
                setCopyOpen(false);
                setUnitId("");
                setConditionGroupId("");
                showSuccess(response.message || "コピー処理が完了しました。");
            } else if (classifySummary(response.summary) === 'all_success') {
                setCopyOpen(false);
                setUnitId("");
                setConditionGroupId("");
                showSuccess(buildResultMessage(response.summary));
            } else {
                setCopyMessage(buildResultMessage(response.summary));
                setFailedItems(failed);
                return;
            }
        } catch (e) {
            setError(toUserMessage(e));
            showError(toUserMessage(e));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box p={2}>
            <Typography>他計算セットからデータをコピーします。</Typography>

            {selectedPath ? (
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    選択中のデータ登録元計算セット：{selectedPath}
                </Typography>
            ) : (
                <Typography variant="subtitle1" sx={{ mb: 2, color: 'error.main' }}>
                    データ登録元計算セットが選択されていません。
                </Typography>
            )}

            {selectedPath && sameAsTarget && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    登録元と登録先が同一です。コピー先を変更してください。
                </Alert>
            )}

            <Stack
                component="form"
                spacing={2}
                noValidate
                autoComplete="off"
                direction="column"
                alignItems="stretch"
            >
                <ProjectSelect atom={projectIdAtom} />
                <DestinationSelect atom={destinationIdAtom} config={fromConfig} />
                <LotSelect atom={lotIdAtom} config={fromConfig} />
                <PhaseSelect atom={phaseIdAtom} config={fromConfig} />
                <CalculationSelect atom={calculationIdAtom} config={fromConfig} />
            </Stack>

            {/* 3つのコピー実行ボタン */}
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <Button
                    variant="contained"
                    sx={{ minWidth: 140 }}
                    // #20: 全データコピーはコピー元の全 gear を列挙するため、選択 gear に依存しない
                    disabled={!targetConfigComplete || !sourceConfigComplete || sameAsTarget || loading}
                    onClick={() => {
                        setCopyMode("all");
                        setCopyOpen(true);
                    }}
                >
                    {loading ? "コピー中..." : "全データコピー"}
                </Button>
                <Button
                    variant="contained"
                    sx={{ minWidth: 140 }}
                    disabled={!targetConfigComplete || !sourceConfigComplete || sameAsTarget || loading || (isHev && !gearId)}
                    onClick={() => {
                        setCopyMode("unit");
                        setCopyOpen(true);
                    }}
                >
                    {loading ? "コピー中..." : "ユニット別データコピー"}
                </Button>
                <Button
                    variant="contained"
                    sx={{ minWidth: 140 }}
                    disabled={!targetConfigComplete || !sourceConfigComplete || sameAsTarget || loading || (isHev && !gearId)}
                    onClick={() => {
                        setCopyMode("sub");
                        setCopyOpen(true);
                    }}
                >
                    {loading ? "コピー中..." : "個別データコピー"}
                </Button>
            </Stack>

            {isHev && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    HEV: 全データコピーはコピー元の登録済み全 Gear をまとめてコピーします（Gear 選択不要）。ユニット別・個別はコピー先＝選択中の Gear（{gearId ?? '未選択'}）、コピー元 Gear はダイアログで指定します。BEV↔HEV 間のコピーはできません。
                </Typography>
            )}

            {/* ★ 個別コピー用ダイアログ */}
            <Dialog open={copyOpen} onClose={handleDialogClose} fullWidth maxWidth="xs">
                <DialogTitle>
                    {copyMode === "all"
                        ? ""
                        : copyMode === "unit"
                            ? "Unit IDを選択してください。"
                            : "Unit IDと条件グループIDを選択してください"}
                </DialogTitle>
                <DialogContent>

                    {/* Type */}
                    <FormControl sx={{ mt: 1 }}>
                        <FormLabel id="type-radio-label">Type</FormLabel>
                        <RadioGroup
                            row
                            aria-labelledby="type-radio-label"
                            value={type}
                            onChange={(e) => setType(e.target.value as "target" | "measurement")}
                        >
                            <FormControlLabel value="target" control={<Radio />} label="目標値" />
                            <FormControlLabel value="measurement" control={<Radio />} label="設計値" />
                        </RadioGroup>
                    </FormControl>

                    {/* Unit ID */}
                    {copyMode !== "all" && (
                        <FormControl fullWidth sx={{ mt: 1 }}>
                            <InputLabel id="drive-select-label">Unit ID</InputLabel>
                            <Select
                                labelId="drive-select-label"
                                label="Unit ID"
                                value={unit_id}
                                onChange={(e) => setUnitId(e.target.value as string)}
                            >
                                {unitOptions.map(({ value, label }) => (
                                    <MenuItem key={value} value={value}>{label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {/* 条件グループID */}
                    {copyMode === "sub" && (
                        <FormControl fullWidth sx={{ mt: 2 }}>
                            <InputLabel id="phenomenon-group-label">条件グループID</InputLabel>
                            <Select
                                labelId="phenomenon-group-label"
                                value={conditionGroupId}
                                onChange={(e) => setConditionGroupId(e.target.value as string)}
                            >
                                {groupOptions.map(({ value, label }) => (
                                    <MenuItem key={value} value={value}>
                                        {label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {/* HEV unit/sub: コピー元 gear（body.config.gear_id）。選択肢はコピー元 calc の登録済み gear。 */}
                    {isHev && copyMode !== "all" && (
                        <FormControl fullWidth sx={{ mt: 2 }}>
                            <InputLabel id="src-gear-label">コピー元 gear</InputLabel>
                            <Select
                                labelId="src-gear-label"
                                label="コピー元 gear"
                                value={srcGearId}
                                onChange={(e) => setSrcGearId(e.target.value as string)}
                            >
                                {GEAR_IDS.filter((g) => fromGears.includes(g)).map((id) => (
                                    <MenuItem key={id} value={id}>{id}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                    {isHev && copyMode !== 'all' && fromGearsStatus === 'success' && fromGears.length === 0 && (
                        <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                            コピー元に登録済みの Gear がありません。
                        </Typography>
                    )}
                    {isHev && copyMode !== 'all' && fromGearsStatus === 'error' && (
                        <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                            コピー元 Gear の取得に失敗しました。時間をおいて再度お試しください。
                        </Typography>
                    )}

                    {copyMessage && (
                        <Box sx={{ mt: 2 }}>
                            <Typography color="error" variant="body2">
                                {copyMessage}
                            </Typography>
                            <FailedItemsList items={failedItems} />
                        </Box>
                    )}

                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose}>キャンセル</Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirm}
                        disabled={
                            (copyMode === "unit" && !unit_id) ||
                            (copyMode === "sub" && (!unit_id || !conditionGroupId)) ||
                            (isHev && copyMode !== "all" && !srcGearId) ||
                            loading ||
                            (isHev && copyMode !== "all" && !gearId)
                        }
                        startIcon={loading ? <CircularProgress size={18} /> : undefined}
                    >
                        {loading ? "コピー中..." : "確定"}
                    </Button>
                </DialogActions>
            </Dialog>

        </Box >
    );
};
